/**
 * Staff help & guides — static content bundled with the app.
 * Keep content aligned with implemented behaviour (see audits/ for the
 * evidence base). Update via normal commits/deploys.
 */

export interface Guide {
  slug: string;
  title: string;
  summary: string;
  category: 'Getting started' | 'Sales workflow' | 'Operations' | 'Marketing' | 'Administration';
  content: string;
}

export const guides: Guide[] = [
  {
    slug: 'getting-started',
    title: 'Getting started',
    summary: 'Signing in, your role, and how the app is organised.',
    category: 'Getting started',
    content: `
## Signing in

Use your staff email and password at the login page. If you've forgotten your password, use **Forgot password** — reset links expire after an hour.

## Roles

What you can see and do depends on your role:

- **Administrator** — everything, including the Admin area (users, lookups, email settings).
- **Sales** — day-to-day selling: leads, clients, quotes, contracts, LOAs, marketing.
- **Sub-broker / Partner / Lead Gen** — an external-facing account. You only see your own dashboard and your own attributed work — not the full client base.

## Navigation

The menu bar along the top is your main navigation:

- **Dashboard** — your renewal pipeline and headline numbers.
- **Contacts** — the leads list (the label says Contacts; it manages sales leads).
- **Clients** — the client base.
- **Quote Tool** — create and manage quotes.
- **Contracts** — utility service contracts.
- **Suppliers** — the supplier catalogue.
- **Marketing** — subscribers, blogs and email campaigns.
- **Company Checker** — live Companies House lookups (admins).
- **Admin** — users, lookup values, email configuration (admins).

## Notifications

The bell icon shows unread notifications; the banner shows system-wide announcements.

## Getting help

You're reading it — **Help & Guides** covers every process in the app. For technical issues, the **Support** button (top right) opens a support portal session that captures your current page automatically.
`,
  },
  {
    slug: 'leads',
    title: 'Leads & conversion',
    summary: 'Creating leads, importing lists, and converting a lead into a client.',
    category: 'Sales workflow',
    content: `
## Where leads live

The **Contacts** menu item opens the leads list — this is your sales pipeline before a company becomes a client.

## Creating a lead

Create a lead with the company name, contact details and any notes. Leads can also be assigned a partner/sub-broker attribution.

## Converting a lead to a client

Use the convert action on a lead. This creates a new **Client** record, copies the contact details across, and marks the lead as converted — a converted lead can't be converted again.

If a client with a similar name already exists you'll see a **possible duplicates** warning. It's advisory — check before confirming, since duplicate clients split contract history and commissions.

## What carries over

- The lead's contact name becomes the client's owner name.
- The lead is stamped with a link to the new client, so attribution stays traceable.
`,
  },
  {
    slug: 'clients',
    title: 'Clients',
    summary: 'The client record, owners vs contacts, Companies House data, and archiving.',
    category: 'Sales workflow',
    content: `
## The client record

A client is the legal entity you contract with. The record holds the company details, owner information, contacts, properties, quotes, contracts, LOAs, documents and activity history.

## Owner vs contacts

Every client has a **PRIMARY contact** created from the owner details. You can add additional contacts (e.g. a site manager per property) with their own name, position, email and phone. Contacts with the **PRIMARY** role receive LOA and formal correspondence by default; you can pick a different contact when issuing.

## Companies House data

The **Companies House** action refreshes the registered name, company number and registered address. Every refresh records a before/after entry in the client's identity audit so you can see exactly what changed. Signed documents (LOAs, quotes) are never affected — they freeze the details at issue time.

## Archive vs delete

- **Archive** hides a client from day-to-day lists while keeping everything — preferred for dormant relationships.
- **Delete** is only possible for a client with no history. Once a client has properties, quotes, contracts, LOAs or documents, deletion is blocked to protect that history.
`,
  },
  {
    slug: 'properties',
    title: 'Properties & sites',
    summary: 'Sites and meters under a client, and how property contacts work.',
    category: 'Sales workflow',
    content: `
## What a property is

A property (or site) is a physical location under a client — each has an address, and typically one or more meters (MPAN for electricity, MPRN for gas, SPID for water).

## Contacts on a property

A property can name its own contact (e.g. a site manager). Where a property contact exists, contract and LOA paperwork for that site can be directed there rather than the client's primary contact.

## Deleting a property

A property that's referenced by contracts or quotes can't be deleted — clean up or close those records first.
`,
  },
  {
    slug: 'contracts',
    title: 'Contracts & the lifecycle',
    summary: 'Utility service statuses, automatic expiry, renewals, and what "live" means.',
    category: 'Operations',
    content: `
## What a contract is

A contract (utility service) records a supply agreement for a property's meter: supplier, service type (Electricity / Gas / Water / Telecoms / Waste), rates, contract period and end date.

## Status lifecycle

The typical journey:

1. **Signed - Pending** — paperwork done, awaiting registration.
2. **Locked in / Awaiting Live** — accepted by the supplier, waiting for supply start.
3. **Live** — the contract is in force. Only **Live** contracts count as live in dashboards.

Terminal states: **EXPIRED**, **Cancelled**, **Lost**, **Terminated Prior to end**, **Objection**, **COT / COO**, **Renewed Awaiting Live**.

## Expiry — automatic

A nightly job expires any **Live** contract whose **end date has passed** (on the end date, not after a grace period). It records the expiry in the client's activity feed. Nothing is deleted — the contract just changes status.

## Where expired contracts go

An expired contract stays on the **renewals dashboard** for **60 days after its end date** so you can chase a renewal. After that it drops out of the queue but remains on the client's record permanently.

## Renewals

When you create a new contract for the same meter starting on or within 90 days of the previous contract's end date, the system links it as a renewal automatically. When the new contract goes Live, the predecessor is marked **Renewed**.
`,
  },
  {
    slug: 'quotes',
    title: 'Quotes & versions',
    summary: 'Building a quote, issuing it, supplier selection, and how versions work.',
    category: 'Sales workflow',
    content: `
## Creating a quote

From a client's record or the **Quote Tool**, pick the property and meter, enter the current supply details and consumption, then add supplier rates (standing charge, unit rates, contract period). The system calculates annual/monthly expenditure and the percentage saving against the current spend.

> If the current expenditure is zero, no percentage difference exists — the quote shows "N/A" rather than a misleading figure.

## Issuing a quote

When you issue/send a quote, the system freezes a **snapshot** of the client and property details and generates a PDF. From then on, every download serves that same frozen document — later edits to the client or property never change what the customer received.

## Versions

Editing an issued quote creates a new **version** (v2, v3…). All versions stay visible on the quote; the current version is marked. Analytics count the *opportunity* once regardless of how many versions exist.

## Supplier selection → contract

Selecting a supplier rate on an issued quote creates the contract (utility service) and completes the quote. Each quote produces at most one contract — trying to select a second rate is rejected.

## Resending

Resend re-sends the **same** issued PDF and link — it doesn't regenerate anything, so what the customer sees never drifts from what you issued.
`,
  },
  {
    slug: 'loa',
    title: 'Letters of Authority (LOA)',
    summary: 'Issuing an LOA, the signing link, resending, versions, and what signers see.',
    category: 'Sales workflow',
    content: `
## What an LOA does

A Letter of Authority lets you act for the client with suppliers. Each issued LOA is a **versioned legal document** — frozen at issue time.

## Issuing

From the client's record choose **Issue LOA**, pick the contact who'll sign, and issue. This creates a new LOA version (v1, v2…), generates the PDF from a frozen snapshot of the client and their properties, and emails a secure signing link. If the client already had a pending LOA, that older version is **superseded** — only one can be signed at a time.

> The client's status field is never touched by the LOA process — an issued LOA doesn't move a client out of "active" or a quote stage.

## The signing link

The signer opens the link (no login needed) and sees the LOA document plus a form for their name, position, date of birth and address history. They can sign on screen or decline with a reason. Links expire after 7 days and stop working the moment the LOA is signed, declined, superseded or expired.

## Signed = permanent

Once signed, the signed PDF and the signing evidence (name, DoB, address history, timestamp, IP) are sealed into the LOA record and can't be altered. Editing the client afterwards never changes a signed LOA.

## Delivery vs the document

If the email fails to send, the LOA itself still exists — use **Resend** on the client record to send the *same* link again. Resend never creates a new version or changes the link.

## When to issue a new version

Signed or declined LOAs are final. If details were wrong, issue a **new version** — the old one stays on the record as evidence and the new one gets its own link and number.
`,
  },
  {
    slug: 'documents',
    title: 'Documents',
    summary: 'The document library and system-generated paperwork.',
    category: 'Operations',
    content: `
## The document library

Every client has a documents section for uploaded files — contracts, correspondence, anything you need on file. Documents can also sit against properties.

## System documents

Issued quotes and LOAs save their PDFs into the library automatically as **system documents**. These are generated artefacts — treat them as read-only evidence of what was sent or signed.

## Uploads

Upload accepts images and PDFs. Files are stored per client and listed newest first.
`,
  },
  {
    slug: 'dashboard-renewals',
    title: 'Dashboard & renewals queue',
    summary: 'Reading the dashboard, the renewals window, and headline numbers.',
    category: 'Operations',
    content: `
## Your dashboard

The dashboard shows headline counts and the renewals queue — contracts approaching or just past their end date.

## The renewals window

The queue shows contracts whose end date falls between **60 days ago** and **90 days ahead**, in these statuses:

- Live, Signed - Pending, Locked in / Awaiting Live, Renewed Awaiting Live — upcoming and in-flight renewals.
- **EXPIRED** — ended within the last 60 days; these are your lapsed-renewal chase list.

Contracts outside the window, and those in Lost / Cancelled / Terminated / Objection / COT-COO states, don't appear.

## Work the queue

Items are sorted by end date — earliest first. For each row, open the client to quote a renewal, or record the outcome (Lost, Cancelled) so it stops appearing.
`,
  },
  {
    slug: 'suppliers-lookups',
    title: 'Suppliers & lookups',
    summary: 'The supplier catalogue and the picklists that drive forms.',
    category: 'Operations',
    content: `
## Suppliers

The supplier catalogue lists the utility providers you quote against — each with their name and any rate metadata. Supplier rates on quotes reference this catalogue.

## Lookups (admin)

**Admin → Lookups** manages the picklists used across the app: utility service statuses, meter types (MPAN/MPRN/SPID), service types, industries, positions, partner names and sub-broker levels.

> Values in use by real records can't be deleted — deactivate them instead to stop new selections while keeping history intact.
`,
  },
  {
    slug: 'marketing',
    title: 'Marketing',
    summary: 'Subscribers, blog posts, and email campaigns.',
    category: 'Marketing',
    content: `
## Subscribers

The mailing list — people opted in via the website, enquiry forms, or the LOA signing form (which offers a newsletter opt-in at signing). Each subscriber has an unsubscribe link baked into their record.

## Blogs

Write blog posts in markdown and publish them to the website. Posts can be drafted, published and un-published; the public site only shows published content.

## Campaigns

Send an email campaign to the subscriber list. Always check content before sending — campaigns go out via Brevo (Sendinblue) using the configured sender address.

## Delivery tracking

The email log records delivery, opens and bounces reported back by Brevo — useful for checking a campaign actually landed.
`,
  },
  {
    slug: 'company-checker',
    title: 'Company Checker',
    summary: 'Live Companies House lookups and refreshing client identity.',
    category: 'Administration',
    content: `
## What it does

**Company Checker** (admin) searches Companies House live: company name, number, registered address, status, incorporation date.

## Using it on a client

From a client's record, the Companies House refresh pulls the registered details in. Every refresh is audited — the identity audit on the client shows before/after values and who triggered it, so you can always see when official data changed a record.

> Refreshing only updates the client's current details — documents already issued keep the details frozen at the time they were produced.
`,
  },
  {
    slug: 'administration',
    title: 'Administration',
    summary: 'Users, roles, email settings, and audit surfaces.',
    category: 'Administration',
    content: `
## Users & roles

**Admin** manages staff accounts and roles (administrator, sales, user, partner, lead_gen). Sub-broker accounts carry a level — Lead Gen, Sub Broker, or Sub Broker Pro — and see only their own dashboard and attributed work.

Deactivating a user removes their access without losing their attribution history.

## Email settings

Admin configures the Brevo (Sendinblue) API key and sender address used for LOAs, quotes, campaigns and notifications. The API key is write-only — it's never displayed back.

## Activity & audit trails

Every client record has an **activity feed** covering key events — LOA issued/signed/expired, contract expiry, status changes. Signed paperwork carries its own evidence (who, when, signer details) independent of the client's live data.

## Where things live

- **Users/lookups/email** — Admin area.
- **Audit findings** — engineering-side in the repo's \`audits/\` folder (technical, not a staff surface).
`,
  },
];

export const getGuide = (slug: string) => guides.find(g => g.slug === slug);
